import { IFareCart, ICartItemData, IUpdateItem } from "@interfaces";
import _ from "lodash";
import moment from "moment";
import styled from "styled-components";
import { getCartItemFareRRP } from "src/Module/Checkout/fareRRP";

interface CartItemRowProps {
    item: IFareCart;
    quantity: number;
    onQuantityChange: (update: IUpdateItem) => void;
    handleDeletePopUp: (id: string | number) => void;
}

// The cart response carries the unit price under `productPricingData` for
// range-priced items (checkavailabilityrange / checkAvailabilityProduct, e.g.
// RespaxV2 grouped products like Uncle Brians) and under `FarePrice` for
// product-and-range items (e.g. SiteMinder). Pick the branch from the actual
// availability SHAPE — the same discriminator `checkoutHelpers.isAvailabiltyByRange`
// uses — not a providerId/groupFaresForAvailabilityCheck heuristic. The old
// heuristic mis-routed grouped range-priced products to the (absent) FarePrice
// branch, so they displayed 0.00 in the cart while checkout priced them correctly.
//
// Combined-price-rule (CPR) fares add to the cart with `pendingAvailabilityCheck:
// true` and omit BOTH productPricingData and FarePrice — the unit RRP is carried
// only in `availability.totalPrice` (a unit price; it does NOT scale with
// quantity). Fall back to it so these lines price correctly instead of 0.00.
export const calculateUnitPrice = (item: IFareCart): number => {
    const availability = _.get(item, "availability") as unknown;
    const isByRange =
        typeof availability === "object" &&
        availability !== null &&
        "productPricingData" in availability &&
        !_.isNil((availability as { productPricingData: unknown }).productPricingData);
    const structuredRRP = isByRange
        ? _.get(item, "availability.productPricingData.RRP", null)
        : _.get(item, "availability.FarePrice.RRP", null);
    if (!_.isNil(structuredRRP)) return structuredRRP;
    const pendingUnitRRP = _.get(item, "availability.totalPrice", null);
    if (!_.isNil(pendingUnitRRP)) return pendingUnitRRP;
    // Last resort: the fare's cached list RRP from `product.json.faresprices`.
    // When the live availability check fails server-side (e.g. TDMS "Failed to
    // load booking details" for a CPR/Generic fare), the item carries NO price
    // in `availability.*` — but the cached product still holds the fare's RRP.
    return getCartItemFareRRP(item as unknown as ICartItemData) ?? 0;
};

export const CartItemComponent = ({
    item,
    quantity,
    handleDeletePopUp,
    onQuantityChange,
}: CartItemRowProps) => {
    const maximumAllowedQuantity = _.get(item, "availability.NumAvailable", 0);
    const unitPrice = calculateUnitPrice(item);

    const handleQuantityUpdate = (proposedQuantity: number) => {
        if (proposedQuantity < 1) {
            handleDeletePopUp(item.id);
            onQuantityChange({
                cartItemId: item.id,
                quantity: 0,
                bookingData: [],
            });
        }
        const validatedQuantity = _.clamp(proposedQuantity, 1, maximumAllowedQuantity);

        const bookingDetails = _.range(validatedQuantity).map((quantityIndex) => ({
            quantityIndex: quantityIndex + 1,
            timeId: _.get(
                item,
                ["booking_data", quantityIndex, "timeId"],
                item.availability.BookingTimeID,
            ),
            redeemers: _.get(item, ["booking_data", quantityIndex, "redeemers"], []),
            pickupId: _.get(item, ["booking_data", quantityIndex, "pickupId"], null),
            pickupLocation: _.get(item, ["booking_data", quantityIndex, "pickupLocation"], ""),
            pickupTime: _.get(item, ["booking_data", quantityIndex, "pickupTime"], ""),
        }));
        onQuantityChange({
            cartItemId: item.id,
            quantity: validatedQuantity,
            bookingData: bookingDetails,
        });
    };

    return (
        <CartItemRowContainer key={item.id}>
            <DateText>({moment(item.booking_date).format("MMM D")})</DateText>
            <PriceContainerDiv>
                <div className="price">
                    <p>Price</p> <span>${(unitPrice * quantity).toFixed(2)}</span>
                </div>
                <div className="quantity">
                    <p>Qty</p>
                    <button type="button" onClick={() => handleQuantityUpdate(quantity - 1)}>
                        <img src="/images/decrease.svg" alt="decrease" />
                    </button>
                    <div>{quantity}</div>
                    <button type="button" onClick={() => handleQuantityUpdate(quantity + 1)}>
                        <img src="/images/increase.svg" alt="increase" />
                    </button>
                </div>
            </PriceContainerDiv>
        </CartItemRowContainer>
    );
};
const CartItemRowContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
`;

const PriceContainerDiv = styled.div`
    border: 1px solid #c3c9d0;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    padding: 8px 14px;
    align-items: center;

    .price {
        display: flex;
        flex-direction: column;
        color: #6e7375;
        font-size: 12px;
        font-family: Plus Jakarta Sans;
        span {
            color: #000;
            font-size: 18px;
        }
    }

    .quantity {
        color: #6e7375;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        p {
            font-family: Plus Jakarta Sans;
        }
        button {
            display: flex;
            align-items: center;
        }
        div {
            font-size: 20px;
            color: #1f2937;
            width: 18px;
            text-align: center;
        }
    }
`;
const DateText = styled.p`
    font-size: 12px;
    font-weight: 600;
    font-family: Plus Jakarta Sans;
    color: #232b2b;
    margin: 4px 0px 8px 0px;
    text-decoration: underline;
    text-underline-offset: 3px;
`;
